import React, { useEffect, useRef } from 'react'

// Linha de batimento: um pico para cada mensagem que SAIU, no horário real da
// saída (sendAt que o pulso traz). Não é enfeite — é a vazão desenhada.
//
// O pulso chega a cada ~5s (mais até 3s de cache no servidor). Se a linha
// terminasse em "agora", os picos surgiriam do nada no meio do desenho a cada
// batida. Por isso ela corre ATRASADA: o fim da linha é "agora menos 8s", e cada
// saída entra pela borda direita no seu tempo, como num monitor de verdade.
const JANELA_MS = 24000
const ATRASO_MS = 8000
const GUARDA_MS = JANELA_MS + ATRASO_MS + 5000

function prefereMenosMovimento() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch (e) {
    return false
  }
}

export default function LinhaDeBatimento({ saidas = [], cor, grade, animado = true, tracejado = false, altura = 58 }) {
  const canvasRef = useRef(null)
  const batidas = useRef(new Set())
  const quadro = useRef(0)

  // Junta as saídas de cada pulso ao histórico (o pulso só traz os últimos 90s).
  useEffect(() => {
    const agora = Date.now()
    ;(saidas || []).forEach((s) => {
      const t = new Date(s).getTime()
      if (Number.isFinite(t)) batidas.current.add(t)
    })
    batidas.current.forEach((t) => { if (agora - t > GUARDA_MS) batidas.current.delete(t) })
  }, [saidas])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined
    const parado = !animado || prefereMenosMovimento()

    const desenhar = () => {
      const dpr = window.devicePixelRatio || 1
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      if (!w) return
      if (canvas.width !== Math.round(w * dpr)) {
        canvas.width = Math.round(w * dpr)
        canvas.height = Math.round(h * dpr)
      }
      const ctx = canvas.getContext('2d')
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)

      ctx.strokeStyle = grade
      ctx.lineWidth = 1
      for (let y = h * 0.25; y < h; y += h * 0.25) {
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(w, y)
        ctx.stroke()
      }

      const fim = Date.now() - ATRASO_MS
      const base = h * 0.72
      const lista = parado ? [] : Array.from(batidas.current)
      ctx.beginPath()
      for (let x = 0; x <= w; x += 2) {
        const t = fim - JANELA_MS * (1 - x / w)
        let y = base + (parado ? 0 : Math.sin(t / 260) * 1.2)
        for (let i = 0; i < lista.length; i++) {
          const d = (t - lista[i]) / 1000
          if (d > -0.05 && d < 0.5) {
            y -= Math.exp(-Math.pow((d - 0.08) * 14, 2)) * h * 0.55 - Math.exp(-Math.pow((d - 0.22) * 16, 2)) * h * 0.14
          }
        }
        if (x === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      }
      ctx.strokeStyle = cor
      ctx.lineWidth = 2
      ctx.lineJoin = 'round'
      ctx.shadowColor = cor
      ctx.shadowBlur = parado ? 0 : 8
      ctx.setLineDash(tracejado ? [5, 5] : [])
      ctx.stroke()
      ctx.shadowBlur = 0
      ctx.setLineDash([])

      ctx.beginPath()
      ctx.arc(w - 3, base, 3, 0, Math.PI * 2)
      ctx.fillStyle = cor
      ctx.fill()
    }

    if (parado) {
      desenhar()
      const aoRedimensionar = () => desenhar()
      window.addEventListener('resize', aoRedimensionar)
      return () => window.removeEventListener('resize', aoRedimensionar)
    }

    const laco = () => {
      if (!document.hidden) desenhar()
      quadro.current = requestAnimationFrame(laco)
    }
    quadro.current = requestAnimationFrame(laco)
    return () => cancelAnimationFrame(quadro.current)
  }, [cor, grade, animado, tracejado])

  return <canvas ref={canvasRef} className="block w-full" style={{ height: altura }} aria-hidden="true" />
}
